import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Activity, BarChart3, History, LineChart, LogOut, Settings as SettingsIcon, Wallet, Zap } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: Activity, testId: "nav-dashboard" },
  { to: "/trade", label: "Trade", icon: LineChart, testId: "nav-trade" },
  { to: "/strategy", label: "MACD Bot", icon: Zap, testId: "nav-strategy" },
  { to: "/backtest", label: "Backtest", icon: History, testId: "nav-backtest" },
  { to: "/portfolio", label: "Portfolio", icon: Wallet, testId: "nav-portfolio" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

export default function Layout({ children, mode, demo }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pendingOpt, setPendingOpt] = React.useState(0);

  React.useEffect(() => {
    const fetchPending = () => {
      import("@/lib/api").then(({ api }) =>
        api.get("/strategy/optimizer-pending-count").then((r) => setPendingOpt(r.data.pending || 0)).catch(() => {})
      );
    };
    fetchPending();
    const id = setInterval(fetchPending, 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen flex bg-[#09090B] text-white">
      {/* Sidebar */}
      <aside className="w-56 border-r border-[#27272A] flex flex-col" data-testid="sidebar">
        <div
          className="px-5 py-5 border-b border-[#27272A] cursor-pointer"
          onClick={() => navigate("/")}
          data-testid="brand-logo"
        >
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-[#00E676] pulse" />
            <span className="mono text-[15px] tracking-[0.18em] font-bold">ALPAC/X</span>
          </div>
          <div className="mono text-[10px] text-[#71717A] mt-1 tracking-[0.2em]">TRADING TERMINAL</div>
        </div>

        <nav className="flex-1 py-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-none ${
                  isActive
                    ? "border-[#00E676] bg-[#141415] text-white"
                    : "border-transparent text-[#A1A1AA] hover:text-white hover:bg-[#141415]"
                }`
              }
            >
              <n.icon size={15} strokeWidth={1.6} />
              <span className="tracking-wide flex-1">{n.label}</span>
              {n.to === "/backtest" && pendingOpt > 0 && (
                <span className="bg-[#00E676] text-black text-[9px] mono px-1.5 py-0.5 font-bold" data-testid="pending-opt-badge">
                  {pendingOpt}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-5 py-4 border-t border-[#27272A]">
          <div className="text-[10px] mono tracking-widest text-[#71717A]">SIGNED IN AS</div>
          <div className="text-sm truncate" data-testid="user-email">{user?.email}</div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="mt-3 flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-[#FF3B30]"
          >
            <LogOut size={13} /> SIGN OUT
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <div className="h-12 border-b border-[#27272A] flex items-center justify-between px-6">
          <div className="flex items-center gap-3 mono text-[11px] tracking-[0.2em] text-[#71717A]">
            <BarChart3 size={13} />
            <span>{mode === "live" ? "LIVE TRADING" : "PAPER TRADING"}</span>
            {demo && <span className="text-[#FF3B30]">• DEMO MODE (NO ALPACA KEYS)</span>}
          </div>
          <div className="flex items-center gap-2 mono text-[11px] text-[#A1A1AA]">
            <span className="inline-block w-1.5 h-1.5 bg-[#00E676] pulse" />
            <span className="tracking-widest">CONNECTED</span>
          </div>
        </div>
        <div className="flex-1 overflow-auto">{children}</div>
      </main>
    </div>
  );
}
